import { ONE_DAY_MS } from '@eflight/shared';
import { API } from 'aws-amplify';
import * as uuid from 'uuid';

import { type QueryResult } from '../../Admin/Datasources/QueryFactory';
import { type CreateErrorReportInput } from '../../API';
import { getDb } from '../../Datasource/LocalDB/LocalDB';
import { createErrorReport } from '../../graphql/mutations';
import { type ErrorLog } from './types';

export const logErrorLocal = (message: string, place: string, additionalData?: any) => {
    const log: ErrorLog = {
        id: uuid.v4(),
        message,
        place,
        date: new Date(),
        additionalData,
    };
    getDb().errorLogs.put(log);
};

export const logErrorRemote = async (message: string, place: string, additionalData?: any) => {
    if (window.location.hostname === 'localhost') {
        return;
    }
    const input: CreateErrorReportInput = {
        message,
        place,
        // Required by the schema: without it AppSync rejects every report that has no additional data.
        data: JSON.stringify(additionalData ?? null),
    };
    const query = createErrorReport;
    const variables = { input };
    const result = (await API.graphql({ query, variables })) as QueryResult<any>;
    if (result.errors?.length) {
        throw result.errors;
    }
};

export const logError = (message: string, place: string, additionalData?: any) => {
    if ((typeof message as any) === 'string' && message.startsWith('Network Error')) {
        return;
    }
    try {
        logErrorLocal(message, place, additionalData);
    } catch (e) {
        console.error('Error when locally saving error');
        console.error(e);
    }
    logErrorRemote(message, place, additionalData).catch((e) => {
        console.error('Error when remotely saving error');
        console.error(e);
    });
};

export const logErrorEvent = (event: ErrorEvent) => {
    const data = {
        lineno: event.lineno,
        colno: event.colno,
        filename: event.filename,
        errorStack: event.error?.stack,
    };
    logError(event.message, 'error-event', data);
};

export const logRejectionEvent = (event: PromiseRejectionEvent) => {
    const reason: unknown = event.reason;
    const message =
        reason instanceof Error ? reason.message : typeof reason === 'string' ? reason : safeStringify(reason);
    logError(message, 'unhandled-rejection', { errorStack: reason instanceof Error ? reason.stack : undefined });
};

const safeStringify = (value: unknown): string => {
    try {
        return JSON.stringify(value) ?? String(value);
    } catch {
        return String(value);
    }
};

const ERROR_LOG_RETENTION_MS = 30 * ONE_DAY_MS;

// errorLogs is left out of wipe() (the error page points to it after an erase), so it needs its own bound
export const pruneErrorLogs = async () => {
    const cutoff = Date.now() - ERROR_LOG_RETENTION_MS;
    await getDb()
        .errorLogs.filter((log) => new Date(log.date).getTime() < cutoff)
        .delete();
};
